import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell } from "@/components/page-shell";
import {
  ArticleBody,
  DotList,
  EndNote,
  MoreLinks,
  Prose,
  ReviewedDate,
  Section,
  SourcesAndDisclaimer,
  Steps,
} from "@/components/guide-page";
import { Checklist } from "@/components/checklist";
import type { TocItem } from "@/components/toc";
import { Faq, faqJsonLd } from "@/components/faq";
import { SourceList } from "@/components/source-list";
import { absUrl, ogImageUrl } from "@/lib/site";
import { medicalWebPageJsonLd, breadcrumbJsonLd } from "@/lib/jsonld";
import { pageTitle } from "@/lib/seo";

export const REVIEWED = "2026-07-20";
const OG = ogImageUrl({
  title: "Alpha-gal syndrome",
  subtitle: "The tick-linked red meat allergy",
  kind: "guide",
});

export const FAQ = [
  {
    q: "How long after eating meat do alpha-gal reactions start?",
    a: "Usually about 2 to 6 hours after eating meat from a mammal, and sometimes later. Most food allergies act much faster, which is why the link to red meat is easy to miss.",
  },
  {
    q: "Does every Lone Star tick bite cause alpha-gal syndrome?",
    a: "No. Most people bitten by a Lone Star tick never get AGS. Some get it after one bite and some after many. Only a doctor can diagnose it.",
  },
  {
    q: "Is there a cure for alpha-gal syndrome?",
    a: "There is no cure yet. Most people manage it by not eating mammal meat and other products that set it off. For some it gets better over several years if they avoid more tick bites. An allergist can help you make a plan.",
  },
  {
    q: "Which foods and products can contain alpha-gal?",
    a: "Beef, pork, lamb, venison and other meat from mammals. Also some dairy, gelatin, and some medicines or supplements made from mammals. Not everyone reacts to all of them. A doctor can help you work out what to avoid.",
  },
];

export const Route = createFileRoute("/alpha-gal")({
  head: () => ({
    meta: [
      { title: pageTitle("Alpha-Gal Syndrome: The Tick-Linked Meat Allergy") },
      {
        name: "description",
        content:
          "Alpha-gal syndrome is a red meat allergy linked to Lone Star tick bites. Reactions usually start 2 to 6 hours after eating. Triggers, tests and what to ask a doctor.",
      },
      { property: "og:title", content: "Alpha-Gal Syndrome: The Tick-Linked Meat Allergy" },
      {
        property: "og:description",
        content: "Educational beginner guide to alpha-gal syndrome and Lone Star tick bites.",
      },
      { property: "og:url", content: absUrl("/alpha-gal") },
      { property: "og:type", content: "article" },
      { property: "og:image", content: OG },
      { name: "twitter:title", content: "Alpha-Gal Syndrome: The Tick-Linked Meat Allergy" },
      { name: "twitter:image", content: OG },
    ],
    links: [{ rel: "canonical", href: absUrl("/alpha-gal") }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          medicalWebPageJsonLd({
            name: "Alpha-Gal Syndrome: The Tick-Linked Meat Allergy",
            description:
              "Educational overview of alpha-gal syndrome (AGS): what it is, why symptoms are delayed, common triggers, and what to ask a clinician.",
            path: "/alpha-gal",
            lastReviewed: REVIEWED,
            image: OG,
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Tick-borne diseases", path: "/diseases" },
            { name: "Alpha-gal syndrome", path: "/alpha-gal" },
          ]),
        ),
      },
    ],
  }),
  component: AlphaGalPage,
});

const TRIGGERS = [
  "Beef",
  "Pork",
  "Lamb",
  "Venison",
  "Gelatin",
  "Some dairy products (varies)",
  "Certain medications and gel capsules",
  "Animal-derived ingredients in food",
];

const SYMPTOMS = [
  "Hives, itching, or rash",
  "Stomach pain, nausea, vomiting, or diarrhea",
  "Shortness of breath, throat tightening",
  "Severe reactions (anaphylaxis) require emergency care",
];

const IF_YOU_SUSPECT = [
  "Note what you ate, how soon symptoms appeared, and any recent tick bites.",
  "Take photos of any rash or hives if possible.",
  "Contact a licensed medical professional.",
  "Seek emergency care for any signs of severe allergic reaction.",
];

const ASK_YOUR_DOCTOR = [
  "Could this be alpha-gal syndrome?",
  "Is alpha-gal testing appropriate for me?",
  "Which foods, medications, or products should I avoid?",
  "Do I need an epinephrine auto-injector?",
  "How should I prepare for future outdoor activities?",
];

const TOC: TocItem[] = [
  { id: "what-it-is", label: "What it is" },
  { id: "delayed", label: "Why symptoms can be delayed" },
  { id: "triggers", label: "Common triggers" },
  { id: "symptoms", label: "Symptoms to watch for" },
  { id: "if-you-suspect", label: "If you suspect it" },
  { id: "ask", label: "What to ask your doctor" },
  { id: "prevent", label: "Reducing tick-bite risk" },
  { id: "common-questions", label: "Common questions" },
];

function AlphaGalPage() {
  return (
    <PageShell
      crumbs={[
        { name: "Home", path: "/" },
        { name: "Tick-borne diseases", path: "/diseases" },
        { name: "Alpha-gal syndrome", path: "/alpha-gal" },
      ]}
      title="Alpha-gal syndrome: the tick-linked meat allergy"
      intro="Why a tick bite can make some people allergic to red meat, what sets off a reaction, and what to ask your doctor."
    >
      <ReviewedDate iso={REVIEWED} />

      <ArticleBody toc={TOC}>
        <Section id="what-it-is" title="What it is">
          <Prose>
            Alpha-gal syndrome (AGS) is an allergy to alpha-gal, a sugar found in most mammals but
            not in people. In some people, a Lone Star tick bite seems to teach the body to react to
            this sugar. After that, eating meat from a mammal, or some other animal products, can
            set off an allergic reaction.
          </Prose>
        </Section>

        <Section id="delayed" title="Why symptoms can be delayed">
          <Prose>
            Most food allergies act fast. An alpha-gal reaction usually comes hours after the meal,
            sometimes in the middle of the night. That delay is why it is easy to miss. A doctor can
            help you put the pieces together.
          </Prose>
        </Section>

        <Section id="triggers" title="Common triggers">
          <DotList items={TRIGGERS} columns />
          <p className="measure text-sm text-muted-foreground">
            Not everyone reacts to all of these. A doctor can help you work out what to avoid.
          </p>
        </Section>

        <Section id="symptoms" title="Symptoms to watch for">
          <DotList items={SYMPTOMS} />
        </Section>

        <Section id="if-you-suspect" title="What to do if you suspect alpha-gal syndrome">
          <Steps items={IF_YOU_SUSPECT} />
        </Section>

        <Section id="ask" title="What to ask your doctor">
          <Checklist items={ASK_YOUR_DOCTOR} className="sm:columns-1" />
        </Section>

        <Section id="prevent" title="How to reduce tick-bite risk">
          <Prose>
            The best thing you can do is avoid more tick bites. The prevention guide covers
            repellent, treated clothing and tick checks, before and after you go outside.
          </Prose>
          <div>
            <Link
              to="/prevention"
              className="inline-flex rounded-sm bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary-dark"
            >
              Open the prevention guide
            </Link>
          </div>
        </Section>

        <Section id="common-questions" title="Common questions">
          <Faq items={FAQ} />
        </Section>

        <SourceList
          ids={["cdc-ags", "cdc-ags-diagnosis", "cdc-ags-distribution", "ncbi-ags-review"]}
          title="Sources"
        />

        <MoreLinks id="keep-reading" title="Keep reading">
          <li>
            <Link to="/$guide" params={{ guide: "alpha-gal-symptoms" }} className="link">
              Alpha-gal symptoms
            </Link>
          </li>
          <li>
            <Link to="/ticks/$species" params={{ species: "lone-star-tick" }} className="link">
              The Lone Star tick
            </Link>
          </li>
          <li>
            <Link to="/tick-bite-tool" className="link">
              What to do after a bite
            </Link>
          </li>
          <li>
            <Link to="/diseases" className="link">
              All tick-borne diseases
            </Link>
          </li>
        </MoreLinks>

        <EndNote>
          This guide is here to help you learn. It is not medical advice, a diagnosis or a
          treatment, so talk to a doctor about your own health. If someone has trouble breathing or
          a severe reaction, call 911. <SourcesAndDisclaimer />
        </EndNote>
      </ArticleBody>
    </PageShell>
  );
}
